import _ from 'lodash'
import type {
    CreateExtArgs,
    CreateExtension,
    DAL,
    DeepFunctionMap,
    Extension,
    ExtensionAPI
} from '@wix/document-manager-core'
import {addComponent, addPage, replaceComponent} from './json/import'
import type {
    CompRef,
    ImportExportOptions,
    RawComponentExport,
    CoreExportFormat,
    WmlExportFormat,
    PredefinedComponentFeaturesGroupsKeys,
    Fragments,
    FragmentsOptions
} from '@wix/document-services-types'
import {
    convertWmlToRawComponentExport,
    convertCoreToRawComponentExport,
    convertRawToCoreComponentExport,
    convertRawToWmlComponentExport,
    convertRawToEmlComponentExport
} from './formatConverter'
import {exportComponent} from './json/export'
import type {EmlExportContext, EmlExportFormat} from '@wix/document-management-eml-converter'
import {converter as wmlConverter} from '@wix/document-management-wml-converter'
import {converter as coreConverter} from '@wix/document-management-core-converter'
import type {BuilderComponentsApi} from '../builderComponents/builderComponents'
import {replaceFragments} from './json/raw/import'
import {exportFragments} from './json/raw/export'

export interface ImportExportApi extends DeepFunctionMap {
    components: {
        replace(componentToReplace: CompRef, componentExport: RawComponentExport, options?: ImportExportOptions): void
        add(
            newComponentPointer: CompRef,
            containerPointer: CompRef,
            componentExport: RawComponentExport,
            options?: ImportExportOptions
        ): CompRef
    }
    pages: {
        replace(pageToReplace: CompRef, pageExport: RawComponentExport, options?: ImportExportOptions): void
        add(newComponentPointer: CompRef, pageExport: RawComponentExport, options?: ImportExportOptions): CompRef
    }
    core: {
        getVersion(): string
        convertCoreToRawComponentExport(componentExport: CoreExportFormat): Promise<RawComponentExport>
        exportComponent(componentPointer: CompRef, options?: ImportExportOptions): CoreExportFormat
        exportPage(pagePointer: CompRef, options?: ImportExportOptions): CoreExportFormat
    }
    wml: {
        getVersion(): string
        convertWmlToRawComponentExport(componentExport: WmlExportFormat): Promise<RawComponentExport>
        exportComponent(componentPointer: CompRef, options?: ImportExportOptions): WmlExportFormat
        exportPage(pagePointer: CompRef, options?: ImportExportOptions): WmlExportFormat
    }
    eml: {
        exportComponent(componentPointer: CompRef, options?: ImportExportOptions): EmlExportFormat
        exportPage(pagePointer: CompRef, options?: ImportExportOptions): EmlExportFormat
    }
}

export interface ImportExportExtensionApi extends ExtensionAPI {
    importExport: ImportExportApi
}

const wrapMethodsWithCustomGetterDisabled = (value: any, dal: DAL): any => {
    if (_.isPlainObject(value)) {
        return _.mapValues(value, prop => wrapMethodsWithCustomGetterDisabled(prop, dal))
    }

    return dal.withoutCustomGetters(value)
}

const getEmlExportContext = (extensionContext: CreateExtArgs, rawExport: RawComponentExport): EmlExportContext => {
    const {extensionAPI} = extensionContext
    const componentsManifest = _.mapValues(rawExport.components, component => {
        const displayName = (extensionAPI.builderComponents as BuilderComponentsApi).getDisplayName(
            component.structure.componentType
        )
        return {
            displayName
        }
    })
    return {componentsManifest}
}

const createExtension: CreateExtension = (): Extension => {
    const createExtensionAPI = (extensionContext: CreateExtArgs): ImportExportExtensionApi => ({
        importExport: {
            components: {
                replace: (
                    componentToReplace: CompRef,
                    rawComponentExport: RawComponentExport,
                    options?: ImportExportOptions
                ): void => replaceComponent(extensionContext, rawComponentExport, componentToReplace, options),
                add: (
                    newComponentPointer: CompRef,
                    containerPointer: CompRef,
                    rawComponentExport: RawComponentExport,
                    options?: ImportExportOptions
                ): CompRef =>
                    addComponent(extensionContext, newComponentPointer, rawComponentExport, containerPointer, options)
            },
            pages: {
                replace: (
                    pageToReplace: CompRef,
                    rawPageExport: RawComponentExport,
                    options?: ImportExportOptions
                ): void => replaceComponent(extensionContext, rawPageExport, pageToReplace, options),
                add: (newComponentPointer, rawPageExport: RawComponentExport, options?: ImportExportOptions): CompRef =>
                    addPage(extensionContext, newComponentPointer, rawPageExport, options)
            },
            core: {
                getVersion: coreConverter.getVersion,
                convertCoreToRawComponentExport,
                exportComponent: (componentPointer: CompRef, options?: ImportExportOptions): CoreExportFormat =>
                    convertRawToCoreComponentExport(exportComponent(extensionContext, componentPointer, options)),
                exportPage: (pagePointer: CompRef, options?: ImportExportOptions): CoreExportFormat =>
                    convertRawToCoreComponentExport(exportComponent(extensionContext, pagePointer, options))
            },
            wml: {
                getVersion: wmlConverter.getVersion,
                convertWmlToRawComponentExport,
                exportComponent: (componentPointer: CompRef, options?: ImportExportOptions): WmlExportFormat =>
                    convertRawToWmlComponentExport(exportComponent(extensionContext, componentPointer, options)),
                exportPage: (pagePointer: CompRef, options?: ImportExportOptions): WmlExportFormat =>
                    convertRawToWmlComponentExport(exportComponent(extensionContext, pagePointer, options))
            },
            eml: {
                exportComponent: (componentPointer: CompRef, options?: ImportExportOptions): EmlExportFormat => {
                    const componentExport = exportComponent(extensionContext, componentPointer, {
                        componentFeatures: 'EMLData' as PredefinedComponentFeaturesGroupsKeys,
                        ...options
                    })
                    return convertRawToEmlComponentExport(
                        componentExport,
                        getEmlExportContext(extensionContext, componentExport)
                    )
                },
                exportPage: (pagePointer: CompRef, options?: ImportExportOptions): EmlExportFormat => {
                    const pageExport = exportComponent(extensionContext, pagePointer, {
                        componentFeatures: 'EMLData' as PredefinedComponentFeaturesGroupsKeys,
                        ...options
                    })
                    return convertRawToEmlComponentExport(pageExport, getEmlExportContext(extensionContext, pageExport))
                }
            },
            fragments: {
                replace: (fragmentsToReplace: Fragments, options?: FragmentsOptions): void =>
                    replaceFragments(extensionContext, fragmentsToReplace, options),
                export: (options?: FragmentsOptions): Fragments => exportFragments(extensionContext, options)
            }
        }
    })

    return {
        name: 'importExport',
        createExtensionAPI: (args: CreateExtArgs): ImportExportExtensionApi =>
            wrapMethodsWithCustomGetterDisabled(createExtensionAPI(args), args.dal)
    }
}

export {createExtension}
